// MessageInput.jsx
import { useState, useRef, useEffect } from "react";
import MicButton from "./MicButton";
import PropTypes from 'prop-types';
import { useDarkMode } from '../contexts/DarkModeContext';
import { Settings, X, Check, Send, Zap, Info, ChevronDown } from 'lucide-react';

const MessageInput = ({
  value,
  onChange,
  onKeyDown,
  onClick,
  isTyping,
  quotedMessage,
  setQuotedMessage,
  setCurrentParentId,
  setInputText,
  AgentSelector,
  analysisMode,
  setAnalysisMode,
}) => {
  const [isMicActive, setIsMicActive] = useState(false);
  // Voice transcript no longer rendered as a separate overlay state;
  // these no-ops keep call sites in MicButton/voice handlers working without changes.
  const setInterimTranscript = () => {};
  const setVoiceText = () => {};
  const [showSettings, setShowSettings] = useState(false);
  const [showModeDropdown, setShowModeDropdown] = useState(false);
  const modeDropdownRef = useRef(null);
  const [hasVoiceInput, setHasVoiceInput] = useState(false);
  const [voiceStartIndex, setVoiceStartIndex] = useState(0);
  const speechRecognitionRef = useRef(null);
  const textareaRef = useRef(null);
  const overlayRef = useRef(null);
  const settingsRef = useRef(null);
  const { isDarkMode } = useDarkMode();

  // Check if we're in production mode - hide dev controls
  // Returns the active colour classes for an analysis mode pill/button
  const analysisModeColors = (mode) => {
    if (mode === "deep") {
      return isDarkMode
        ? 'bg-sky-500/20 text-sky-400 border border-sky-500/40'
        : 'bg-sky-50 text-sky-600 border border-sky-200';
    }
    return isDarkMode
      ? 'bg-amber-500/20 text-amber-400 border border-amber-500/40'
      : 'bg-amber-50 text-amber-600 border border-amber-200';
  };
  // Close settings dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (settingsRef.current && !settingsRef.current.contains(event.target)) {
        setShowSettings(false);
      }
      if (modeDropdownRef.current && !modeDropdownRef.current.contains(event.target)) {
        setShowModeDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Auto-resize textarea when voice text is added
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const newHeight = Math.min(textareaRef.current.scrollHeight, 200);
      textareaRef.current.style.height = newHeight + 'px';
      
      // Auto-scroll to bottom when voice text is being added
      if (hasVoiceInput) {
        setTimeout(() => {
          if (textareaRef.current) {
            textareaRef.current.scrollTop = textareaRef.current.scrollHeight;
          }
        }, 0);
      }
    }
  }, [value, hasVoiceInput]);

  // Sync scroll position between textarea and overlay
  useEffect(() => {
    if (!hasVoiceInput || !textareaRef.current) return;

    const handleScroll = () => {
      if (overlayRef.current && textareaRef.current) {
        overlayRef.current.scrollTop = textareaRef.current.scrollTop;
      }
    };

    const textarea = textareaRef.current;
    textarea.addEventListener('scroll', handleScroll);
    
    return () => {
      textarea.removeEventListener('scroll', handleScroll);
    };
  }, [hasVoiceInput]);
  
  const closeQuotedMessage = () => {
    setQuotedMessage(null); // Remove the quoted message
    setCurrentParentId(null);
  };

  const handleTextareaChange = (e) => {
    // Prevent editing if voice input is active
    if (hasVoiceInput) return;
    
    onChange(e);
    // Auto-resize textarea
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 200) + 'px';
    }
  };

  const handleSendMessage = () => {
    onClick();
    // Reset height after sending
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }, 0);
  };

  const handleConfirmVoice = () => {
    // Stop and destroy recognition instance
    if (speechRecognitionRef.current) {
      speechRecognitionRef.current.onend = null;
      speechRecognitionRef.current.onresult = null;
      speechRecognitionRef.current.onerror = null;
      speechRecognitionRef.current.onstart = null;
      try {
        speechRecognitionRef.current.stop();
      } catch (e) {
        console.error('Recognition already stopped');
      }
      speechRecognitionRef.current = null; // Destroy instance
    }
    
    // Clear interim transcript
    setInterimTranscript("");
    
    // Merge voice text into regular text (remove highlight)
    setHasVoiceInput(false);
    setVoiceText("");
    setIsMicActive(false);
    setVoiceStartIndex(0);
    
    // Auto-resize textarea after confirming
    if (textareaRef.current) {
      setTimeout(() => {
        textareaRef.current.style.height = 'auto';
        textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 200) + 'px';
      }, 0);
    }
  };

  const handleCancelVoice = () => {
    // Stop and destroy recognition instance
    if (speechRecognitionRef.current) {
      speechRecognitionRef.current.onend = null;
      speechRecognitionRef.current.onresult = null;
      speechRecognitionRef.current.onerror = null;
      speechRecognitionRef.current.onstart = null;
      try {
        speechRecognitionRef.current.stop();
      } catch (e) {
        console.error('Recognition already stopped');
      }
      speechRecognitionRef.current = null; // Destroy instance
    }
    
    // Remove ALL voice text from input
    setInputText((prevText) => {
      // Remove voice text starting from voiceStartIndex
      const textBeforeVoice = prevText.substring(0, voiceStartIndex);
      return textBeforeVoice;
    });
    
    // Clear all voice-related state
    setInterimTranscript("");
    setVoiceText("");
    setHasVoiceInput(false);
    setIsMicActive(false);
    setVoiceStartIndex(0);
    
    // Auto-resize textarea after canceling
    if (textareaRef.current) {
      setTimeout(() => {
        textareaRef.current.style.height = 'auto';
        textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 200) + 'px';
      }, 0);
    }
  };

  return (
    <div className="flex flex-col w-full gap-3">
      {/* Quoted Message */}
      {quotedMessage && (
        <div className={`flex items-center p-3 rounded-xl ${
          isDarkMode 
            ? 'bg-slate-700/50 border border-slate-600/40' 
            : 'bg-slate-50/90 border border-slate-200/60'
        }`}>
          <p className="mb-0 flex-grow">
            <span className={`text-xs font-semibold ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              Replying To:
            </span>
            <br />
            <span className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
              {quotedMessage}
            </span>
          </p>
          <button
            onClick={closeQuotedMessage}
            className={`ml-auto w-7 h-7 rounded-md flex items-center justify-center text-lg border-0 bg-transparent transition-colors ${
              isDarkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-600 hover:text-slate-800'
            }`}
          >
            &times;
          </button>
        </div>
      )}

      {/* Main Input Container - GPT/DeepSeek Style */}
      <div className={`rounded-3xl transition-all ${
        isDarkMode 
          ? 'bg-slate-800/95 shadow-[0_4px_20px_rgba(0,0,0,0.3),0_1px_3px_rgba(0,0,0,0.2)]' 
          : 'bg-white shadow-[0_2px_12px_rgba(0,0,0,0.08),0_1px_3px_rgba(0,0,0,0.04)]'
      }`}>
        {/* Textarea - Top Section */}
        <div className="px-4 pt-3 pb-3 relative">
          <div className="relative">
            {hasVoiceInput ? (
              <div className="relative w-full">
                <textarea
                  ref={textareaRef}
                  placeholder="Listening..."
                  value={value}
                  onChange={handleTextareaChange}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                    } else {
                      onKeyDown?.(e);
                    }
                  }}
                  className={`w-full border-0 outline-none resize-none custom-scrollbar transition-colors bg-transparent ${
                    isDarkMode ? 'placeholder:text-slate-500' : 'placeholder:text-slate-400'
                  }`}
                  style={{ 
                    height: "auto",
                    minHeight: "24px",
                    maxHeight: "200px",
                    fontSize: '15px',
                    lineHeight: '1.5',
                    color: 'transparent',
                    caretColor: isDarkMode ? '#fef08a' : '#854d0e',
                    overflowY: 'auto',
                    overflowX: 'hidden'
                  }}
                  rows="1"
                  id="chatInput"
                  readOnly={true}
                />
                <div 
                  ref={overlayRef}
                  className="absolute top-0 left-0 right-0 pointer-events-none whitespace-pre-wrap break-words custom-scrollbar"
                  style={{ 
                    fontSize: '15px',
                    lineHeight: '1.5',
                    maxHeight: '200px',
                    minHeight: '24px',
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    paddingRight: '6px'
                  }}
                >
                  <span className={isDarkMode ? 'text-slate-100' : 'text-slate-900'}>
                    {value.substring(0, voiceStartIndex)}
                  </span>
                  <span className={`rounded-sm px-0.5 ${
                    isDarkMode 
                      ? 'bg-yellow-500/20 text-yellow-200' 
                      : 'bg-yellow-200 text-yellow-900'
                  }`}>
                    {value.substring(voiceStartIndex)}
                  </span>
                </div>
              </div>
            ) : (
              <textarea
                ref={textareaRef}
                placeholder="Type your message..."
                value={value}
                onChange={handleTextareaChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (value.trim() !== '') {
                      handleSendMessage();
                    }
                  } else {
                    onKeyDown?.(e);
                  }
                }}
                className={`w-full bg-transparent border-0 outline-none resize-none custom-scrollbar transition-colors ${
                  isDarkMode ? 'text-slate-100 placeholder:text-slate-500' : 'text-slate-900 placeholder:text-slate-400'
                }`}
                style={{ 
                  height: "auto",
                  minHeight: "24px",
                  maxHeight: "200px",
                  fontSize: '15px',
                  lineHeight: '1.5'
                }}
                rows="1"
                id="chatInput"
              />
            )}
          </div>
        </div>

        {/* Bottom Menu - DeepSeek Style */}
        <div className="flex items-center justify-between px-4 py-2.5">
          {/* Left Side - Controls */}
          <div className="flex items-center gap-2.5">
            {/* Agent Selector (desktop) */}
            {AgentSelector && (
              <div className="hidden md:flex items-center">
                <AgentSelector />
              </div>
            )}
            {/* Mobile view - Settings dropdown button (always visible) */}
            <div className="md:hidden relative" ref={settingsRef}>
              <button
                onClick={() => setShowSettings(!showSettings)}
                className={`h-9 px-3 rounded-xl flex items-center justify-center transition-all ${
                  isDarkMode
                    ? 'bg-slate-800/60 text-slate-300 hover:bg-slate-800/80'
                    : 'bg-slate-50/90 text-slate-600 hover:bg-slate-100'
                }`}
                style={{
                  border: isDarkMode ? '1px solid rgba(51, 65, 85, 0.6)' : '1px solid rgba(226, 232, 240, 0.8)',
                  boxShadow: isDarkMode ? '0 2px 8px rgba(0, 0, 0, 0.2)' : '0 2px 8px rgba(0, 0, 0, 0.05)'
                }}
              >
                <Settings className="w-4 h-4" />
              </button>

              {/* Settings Dropdown */}
              {showSettings && (
                <div className={`absolute bottom-full right-0 mb-2 rounded-xl p-3 min-w-[200px] z-50 ${
                  isDarkMode
                    ? 'bg-slate-800 border border-slate-700/60 shadow-[0_4px_16px_rgba(0,0,0,0.4)]'
                    : 'bg-white border border-slate-200 shadow-[0_4px_16px_rgba(0,0,0,0.1)]'
                }`}>
                  {/* Agent Selector in dropdown */}
                  {AgentSelector && (
                    <div className="mb-3">
                      <label className={`block text-xs font-semibold mb-1.5 ${
                        isDarkMode ? 'text-slate-400' : 'text-slate-600'
                      }`}>
                        Agent
                      </label>
                      <AgentSelector />
                    </div>
                  )}
                  {/* Analysis Mode - always visible in dropdown */}
                  <div>
                    <label className={`block text-xs font-semibold mb-1.5 ${
                      isDarkMode ? 'text-slate-400' : 'text-slate-600'
                    }`}>
                      Analysis Mode
                    </label>
                    {[
                      { mode: 'fast', icon: <Zap className="w-3.5 h-3.5" />, label: 'Fast', desc: 'Quick, concise responses' },
                      { mode: 'deep', icon: <Info className="w-3.5 h-3.5" />, label: 'Thinking', desc: 'Deeper, step-by-step analysis' },
                    ].map(({ mode, icon, label, desc }) => (
                      <button
                        key={mode}
                        onClick={() => { setAnalysisMode(mode); setShowSettings(false); }}
                        className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl mb-1 text-left transition-colors ${
                          analysisMode === mode
                            ? analysisModeColors(mode)
                            : isDarkMode ? 'hover:bg-slate-700/40 text-slate-300' : 'hover:bg-slate-50 text-slate-700'
                        }`}
                      >
                        <span>{icon}</span>
                        <span className="flex-1 min-w-0">
                          <span className="block text-xs font-semibold">{label}</span>
                          <span className={`block text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{desc}</span>
                        </span>
                        {analysisMode === mode && (
                          <Check className={`w-3.5 h-3.5 flex-shrink-0 ${isDarkMode ? 'text-blue-400' : 'text-blue-500'}`} strokeWidth={2.5} />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Side - Action Buttons */}
          <div className="flex items-center gap-2.5">
            {hasVoiceInput ? (
              <>
                {/* Recording Indicator */}
                {isMicActive && (
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                  </div>
                )}
                
                {/* Cancel Voice Button (Cross) */}
                <button
                  onClick={handleCancelVoice}
                  aria-label="Cancel voice input"
                  className={`w-9 h-9 rounded-full flex items-center justify-center transition-all ${
                    isDarkMode
                      ? 'bg-slate-700/60 border border-slate-600/40 text-slate-300 hover:bg-red-500/20 hover:text-red-400'
                      : 'bg-slate-100 border border-slate-200 text-slate-600 hover:bg-red-50 hover:text-red-500'
                  }`}
                >
                  <X className="w-5 h-5" />
                </button>

                {/* Confirm Voice Button (Tick) */}
                <button
                  onClick={handleConfirmVoice}
                  aria-label="Confirm voice input"
                  className={`w-9 h-9 rounded-full flex items-center justify-center transition-all ${
                    isDarkMode
                      ? 'bg-green-500/20 border border-green-500/40 text-green-400 hover:bg-green-500 hover:text-white'
                      : 'bg-green-50 border border-green-200 text-green-600 hover:bg-green-500 hover:text-white'
                  }`}
                >
                  <Check className="w-5 h-5" strokeWidth={3} />
                </button>
              </>
            ) : (
              <>
                {/* Analysis Mode Pill - desktop only, left of mic */}
                <div className="hidden md:block relative" ref={modeDropdownRef}>
                  <button
                    onClick={() => setShowModeDropdown((v) => !v)}
                    className={`h-9 flex items-center gap-1 px-3 rounded-full text-xs font-medium transition-all ${
                      isDarkMode
                        ? 'bg-slate-700/50 text-slate-300 border border-slate-600/50 hover:bg-slate-700/80'
                        : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    {analysisMode === "deep"
                      ? <Info className="w-3.5 h-3.5 flex-shrink-0" />
                      : <Zap className="w-3.5 h-3.5 flex-shrink-0" />
                    }
                    <span>{analysisMode === "deep" ? "Thinking" : "Fast"}</span>
                    <ChevronDown className={`w-3 h-3 flex-shrink-0 transition-transform ${showModeDropdown ? 'rotate-180' : ''}`} />
                  </button>

                  {showModeDropdown && (
                    <div className={`absolute bottom-full right-0 mb-2 rounded-2xl overflow-hidden min-w-[220px] z-50 ${
                      isDarkMode
                        ? 'bg-slate-800 border border-slate-700/60 shadow-[0_8px_24px_rgba(0,0,0,0.5)]'
                        : 'bg-white border border-slate-200/80 shadow-[0_8px_24px_rgba(0,0,0,0.12)]'
                    }`}>
                      {[
                        { mode: 'fast', icon: <Zap className="w-4 h-4" />, label: 'Fast', desc: 'Quick, concise responses' },
                        { mode: 'deep', icon: <Info className="w-4 h-4" />, label: 'Thinking', desc: 'Deeper, step-by-step analysis' },
                      ].map(({ mode, icon, label, desc }) => (
                        <button
                          key={mode}
                          onClick={() => { setAnalysisMode(mode); setShowModeDropdown(false); }}
                          className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                            isDarkMode ? 'hover:bg-slate-700/60 text-slate-200' : 'hover:bg-slate-50 text-slate-800'
                          }`}
                        >
                          <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>{icon}</span>
                          <span className="flex-1 min-w-0">
                            <span className="block text-sm font-semibold leading-tight">{label}</span>
                            <span className={`block text-xs leading-tight mt-0.5 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{desc}</span>
                          </span>
                          {analysisMode === mode && (
                            <Check className={`w-4 h-4 flex-shrink-0 ${isDarkMode ? 'text-blue-400' : 'text-blue-500'}`} strokeWidth={2.5} />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Mic Button */}
                <MicButton
                  isTyping={isTyping}
                  speechRecognitionRef={speechRecognitionRef}
                  setInterimTranscript={setInterimTranscript}
                  setInputText={setInputText}
                  isMicActive={isMicActive}
                  setIsMicActive={setIsMicActive}
                  setVoiceText={setVoiceText}
                  setHasVoiceInput={setHasVoiceInput}
                  setVoiceStartIndex={setVoiceStartIndex}
                  value={value}
                />

                {/* Send Button */}
                <button
                  onClick={handleSendMessage}
                  disabled={isTyping || !value.trim()}
                  className={`w-9 h-9 rounded-full flex items-center justify-center transition-all ${
                    value.trim() && !isTyping
                      ? 'bg-sky-500 text-white hover:bg-sky-600 hover:scale-105 shadow-[0_2px_8px_rgba(14,165,233,0.3)] hover:shadow-[0_4px_12px_rgba(14,165,233,0.4)]'
                      : isDarkMode
                      ? 'bg-slate-700/50 text-slate-600 opacity-60 cursor-not-allowed'
                      : 'bg-slate-200/60 text-slate-400 opacity-60 cursor-not-allowed'
                  }`}
                >
                  <Send className="w-4 h-4" fill="currentColor" />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Add PropTypes validation

MessageInput.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onKeyDown: PropTypes.func,
  onClick: PropTypes.func.isRequired,
  isTyping: PropTypes.bool.isRequired,
  quotedMessage: PropTypes.string,
  setQuotedMessage: PropTypes.func.isRequired,
  setCurrentParentId: PropTypes.func.isRequired,
  setInputText: PropTypes.func.isRequired,
  AgentSelector: PropTypes.elementType,
  analysisMode: PropTypes.oneOf(["deep", "fast"]).isRequired,
  setAnalysisMode: PropTypes.func.isRequired,
};

export default MessageInput;